import {
  ReactElement,
  useEffect,
  useState,
  Fragment,
  useMemo,
  useCallback,
  useRef,
  Ref,
} from "react";
import SearchLayout from "../components/layout/search";
import { GetServerSideProps } from "next";
import { useAuth } from "../hooks/auth";
import Unprotected from "../server/unprotected";
import classNames from "../util/classNames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Disclosure, Menu, Popover, Transition, Tab } from "@headlessui/react";
import { ChevronDownIcon, FilterIcon, StarIcon } from "@heroicons/react/solid";
import { MapIcon, SpeakerphoneIcon, XIcon } from "@heroicons/react/outline";
import { useApi } from "../hooks/api";
import { Review } from "../model/review";
import { usePopper } from "react-popper";
import ErrorCallout from "../components/callouts/error";
import {
  PrepCenter,
  PrepCenterLocation,
  SupportedModel,
  SupportedModelDetails,
  Turnaround,
  TurnaroundSort,
} from "../model/prepCenter";
import Spinner from "../components/spinner";
import MapGL, { MapRef } from "react-map-gl";
import Marker from "../components/marker";
import "mapbox-gl/dist/mapbox-gl.css";
import Link from "next/link";
import Image from "next/image";

// This function takes in latitude and longitude of two location and returns the distance between them as the crow flies (in km)
/*const calcCrow = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  var R = 6371; // km
  var dLat = toRad(lat2 - lat1);
  var dLon = toRad(lon2 - lon1);
  var lat1 = toRad(lat1);
  var lat2 = toRad(lat2);

  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c;
  return d;
};

// Converts numeric degrees to radians
const toRad = (value: number) => (value * Math.PI) / 180;*/

const filtersOld = {
  price: [
    { value: "0", label: "$0 - $25", checked: false },
    { value: "25", label: "$25 - $50", checked: false },
    { value: "50", label: "$50 - $75", checked: false },
    { value: "75", label: "$75+", checked: false },
  ],
  color: [
    { value: "white", label: "White", checked: false },
    { value: "beige", label: "Beige", checked: false },
    { value: "blue", label: "Blue", checked: true },
    { value: "brown", label: "Brown", checked: false },
    { value: "green", label: "Green", checked: false },
    { value: "purple", label: "Purple", checked: false },
  ],
  size: [
    { value: "xs", label: "XS", checked: false },
    { value: "s", label: "S", checked: true },
    { value: "m", label: "M", checked: false },
    { value: "l", label: "L", checked: false },
    { value: "xl", label: "XL", checked: false },
    { value: "2xl", label: "2XL", checked: false },
  ],
  category: [
    { value: "all-new-arrivals", label: "All New Arrivals", checked: false },
    { value: "tees", label: "Tees", checked: false },
    { value: "objects", label: "Objects", checked: false },
    { value: "sweatshirts", label: "Sweatshirts", checked: false },
    { value: "pants-and-shorts", label: "Pants & Shorts", checked: false },
  ],
};
const sortOptions = [
  { name: "Most Popular", href: "#", current: true },
  { name: "Best Rating", href: "#", current: false },
  { name: "Newest", href: "#", current: false },
];

enum YesNoShowAllFilter {
  Yes = "YES",
  No = "NO",
  ShowAll = "SHOW_ALL",
}

enum TurnaroundFilter {
  Within24 = 24,
  Within48 = 48,
  Within72 = 72,
  Over72 = 73,
}

const supportedModelDetails = {
  [SupportedModel.SupportedModelWholesale]: {
    title: "Wholesale",
    description: "High unit volume, often palletized.",
    color: "bg-green-100 text-green-800",
  },
  [SupportedModel.SupportedModelPrivateLabel]: {
    title: "Private label",
    description: "High unit volume, often palletized.",
    color: "bg-blue-100 text-blue-800",
  },
  [SupportedModel.SupportedModelOnlineArbitrage]: {
    title: "Online arbitrage",
    description:
      "Variety of products, ordered online and shipped to the center.",
    color: "bg-purple-100 text-purple-800",
  },
  [SupportedModel.SupportedModelRetailArbitrage]: {
    title: "Retail arbitrage",
    description: "Variety of products.",
    color: "bg-indigo-100 text-indigo-800",
  },
  [SupportedModel.SupportedModelHazmat]: {
    title: "Hazmat",
    description:
      "Dangerous goods that require the seller to have authorization for.",
    color: "bg-red-100 text-red-800",
  },
  [SupportedModel.SupportedModelBooks]: {
    title: "Books",
    description: "New and used book prep.",
    color: "bg-orange-100 text-orange-800",
  },
};

const randomGradientBg = (hashValue: number): string => {
  const gradients: string[] = [
    "from-cyan-500 to-blue-500",
    "from-sky-500 to-indigo-500",
    "from-blue-500 to-cyan-500",
    "from-indigo-500 to-sky-500",
    // "from-violet-500 to-fuchsia-500",
    // "from-purple-500 to-pink-500",
  ];

  return gradients[Math.floor((hashValue / 10000) % gradients.length)];
};

const getLocationInfo = (locations: PrepCenterLocation[] | null): string => {
  if (!locations || locations.length === 0) return "No locations listed";

  if (locations.length === 1)
    return `${locations[0].city ? locations[0].city + ", " : ""}${
      locations[0].state
    }, ${locations[0].country}`;

  return `${locations.length} Location${locations.length > 1 ? "s" : ""}`;
};

function useMapRef() {
  const [mapRef, setMapRef] = useState<MapRef | null>(null);
  const setRef = useCallback((node: MapRef) => {
    if (node !== null) {
      setMapRef(node);
    }
  }, []);
  return [mapRef, setRef];
}

export default function Home() {
  const { authUser, dbUser, prepCenter } = useAuth();
  const { listPrepCenters } = useApi();
  const [prepCenters, setPrepCenters] = useState<PrepCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [highlighted, setHighlighted] = useState("");
  const [prepCentersError, setPrepCentersError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState(1);
  const [mapRef, setRef] = useMapRef();
  const [viewState, setViewState] = useState({
    longitude: -97.9222112121185,
    latitude: 39.3812661305678,
    zoom: 3.3,
  });
  const [filters, setFilters] = useState<{
    acceptingClients: YesNoShowAllFilter;
    liftgateRequired: YesNoShowAllFilter;
    supportedModels: SupportedModel[];
    turnaround: TurnaroundFilter;
  }>({
    acceptingClients: YesNoShowAllFilter.ShowAll,
    liftgateRequired: YesNoShowAllFilter.ShowAll,
    supportedModels: [],
    turnaround: TurnaroundFilter.Over72,
  });

  const clearFilters = useCallback(() => {
    setFilters({
      acceptingClients: YesNoShowAllFilter.ShowAll,
      liftgateRequired: YesNoShowAllFilter.ShowAll,
      supportedModels: [],
      turnaround: TurnaroundFilter.Over72,
    });
  }, [setFilters]);

  const prepCenterComponent = useCallback(
    (prepCenter: PrepCenter) => (
      <li
        key={prepCenter.id}
        onMouseEnter={() => setHighlighted(prepCenter.id)}
        onMouseLeave={() => setHighlighted("")}
      >
        <Link href={`/p/${prepCenter.id}`} passHref>
          <a
            className={classNames(
              "bg-white shadow overflow-hidden rounded-md flex flex-col lg:flex-row",
              prepCenter.description ? "lg:h-48" : "lg:h-36"
            )}
          >
            <div className="relative lg:w-64 h-28 lg:h-full lg:justify-center items-center flex shrink-0">
              <div className="absolute z-20 left-3 lg:left-2 top-3 lg:top-2">
                <span
                  className={classNames(
                    "inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium",
                    prepCenter.acceptingClients == null ? "hidden" : "",
                    prepCenter.acceptingClients
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  )}
                >
                  {`${
                    prepCenter.acceptingClients ? "Accepting" : "Not accepting"
                  } clients`}
                </span>
              </div>

              <div className="z-20 mx-2 mt-20 lg:m-0 px-1 lg:p-0">
                {prepCenter.logo ? (
                  <div
                    className={classNames(
                      "bg-white p-1 shadow-lg lg:shadow-sm h-24 w-24 rounded-md",
                      prepCenter.description
                        ? "lg:h-28 lg:w-28 lg:rounded-lg"
                        : "lg:h-14 lg:w-14 lg:rounded-md"
                    )}
                  >
                    <div className="relative flex h-full w-full rounded-sm">
                      <Image
                        src={prepCenter?.logo}
                        alt={prepCenter?.name}
                        layout="fill"
                        objectFit="contain"
                        className="rounded-md"
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    className={classNames(
                      "z-20 bg-white p-1 shadow-lg lg:shadow-sm h-24 w-24 rounded-md",
                      prepCenter.description
                        ? "lg:h-28 lg:w-28 lg:rounded-lg"
                        : "lg:h-14 lg:w-14 lg:rounded-md"
                    )}
                  >
                    <div className="bg-gray-300 w-full h-full rounded-sm flex">
                      <div className="relative h-1/2 w-1/2 m-auto">
                        <Image
                          src="/images/icon-light.svg"
                          alt={prepCenter?.name}
                          layout="fill"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div
                className={classNames(
                  "absolute w-full h-full bg-gradient-to-r justify-center items-center flex bg-center bg-no-repeat bg-cover",
                  randomGradientBg(prepCenter.created_at.getTime())
                )}
              ></div>

              <div
                style={
                  prepCenter.banner
                    ? {
                        backgroundImage:
                          `url("${prepCenter.banner}")` ?? undefined,
                      }
                    : undefined
                }
                className="absolute opacity-50 w-full h-full bg-center bg-no-repeat bg-cover"
              ></div>
            </div>

            <div className="px-4 py-4 lg:px-6 grow flex flex-col mt-6 lg:m-0">
              <div className="flex justify-between flex-wrap">
                <h3 className="text-lg font-medium flex items-center">
                  {prepCenter.name}
                  {prepCenter.verified && (
                    <>
                      <FontAwesomeIcon
                        icon={["fas", "shield-check"]}
                        className="text-base text-vendrive-base ml-2"
                      />
                      <span className="text-sm ml-1 text-slate-600 font-medium">
                        Verified
                      </span>
                    </>
                  )}
                </h3>
                <span className="text-gray-500 text-base flex items-center">
                  {getLocationInfo(prepCenter.locations)}
                </span>
              </div>

              <div className="flex items-center mt-1">
                {[0, 1, 2, 3, 4].map((rating) => (
                  <StarIcon
                    key={rating}
                    className={classNames(
                      Math.round(prepCenter.averageRating ?? 0) > rating
                        ? "text-yellow-400"
                        : "text-gray-300",
                      "h-5 w-5 flex-shrink-0"
                    )}
                    aria-hidden="true"
                  />
                ))}
                <span className="text-gray-500 text-sm ml-2">
                  {prepCenter.reviewCount
                    ? `${prepCenter.averageRating?.toFixed(2)} (${
                        prepCenter.reviewCount
                      } Review${prepCenter.reviewCount > 1 ? "s" : ""})`
                    : "No reviews"}
                </span>
              </div>

              {/* Tagline (on sm breakpoint) */}
              <p className="lg:hidden mt-2 text-sm font-medium text-gray-600">
                {prepCenter?.tagline}
              </p>

              {/* Supported models */}
              <div className="flex">
                <div className="mt-3 lg:mt-2 flex flex-wrap gap-1">
                  {prepCenter.supportedModels?.map((model) => (
                    <span
                      key={model}
                      className={classNames(
                        "inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium",
                        supportedModelDetails[model].color
                      )}
                    >
                      {supportedModelDetails[model].title}
                    </span>
                  ))}
                </div>
              </div>

              {/* Description (hidden on sm breakpoint) */}
              <div
                className={classNames(
                  "hidden lg:block mt-auto pt-2 text-ellipsis overflow-hidden",
                  prepCenter.description ? "" : "lg:hidden"
                )}
              >
                <dt className="text-sm font-medium text-gray-900">
                  {prepCenter.tagline}
                </dt>
                <dd className="mt-1 text-sm text-gray-500 line-clamp-2">
                  {prepCenter.description}
                </dd>
              </div>
            </div>
          </a>
        </Link>
      </li>
    ),
    [setHighlighted]
  );

  const hasLocationInViewport = useCallback(
    (prepCenter: PrepCenter): boolean => {
      if (!mapRef || !prepCenter || !prepCenter.locations) return false;

      const bounds = (mapRef as MapRef).getMap().getBounds();

      for (let i = 0; i < prepCenter.locations.length; i++) {
        const location = prepCenter.locations[i];
        if (!location.longitude || !location.latitude) continue;

        if (
          bounds.getSouthWest().lng <= location.longitude &&
          location.longitude <= bounds.getNorthEast().lng &&
          bounds.getSouthWest().lat <= location.latitude &&
          location.latitude <= bounds.getNorthEast().lat
        ) {
          return true;
        }
      }

      return false;
    },
    [mapRef]
  );

  /**
   * Filtered prep centers (client-side filtering)
   */
  const filteredPrepCenters = useMemo((): PrepCenter[] => {
    return prepCenters.filter((prepCenter) => {
      // Accepting clients filter
      switch (filters.acceptingClients) {
        case YesNoShowAllFilter.ShowAll:
          break;
        case YesNoShowAllFilter.Yes:
          if (
            prepCenter.acceptingClients != null &&
            !prepCenter.acceptingClients
          ) {
            return false;
          }
          break;
        case YesNoShowAllFilter.No:
          if (
            prepCenter.acceptingClients != null &&
            prepCenter.acceptingClients
          ) {
            return false;
          }
          break;
      }

      // Liftgate required filter
      switch (filters.liftgateRequired) {
        case YesNoShowAllFilter.ShowAll:
          break;
        case YesNoShowAllFilter.Yes:
          if (
            prepCenter.liftgateRequired != null &&
            !prepCenter.liftgateRequired
          ) {
            return false;
          }
          break;
        case YesNoShowAllFilter.No:
          if (
            prepCenter.liftgateRequired != null &&
            prepCenter.liftgateRequired
          ) {
            return false;
          }
          break;
      }

      // Supported models filter
      if (filters.supportedModels.length > 0) {
        if (!prepCenter.supportedModels?.length) return false;

        if (
          !filters.supportedModels.every((v) =>
            prepCenter.supportedModels?.includes(v)
          )
        )
          return false;
      }

      // Turnaround filter
      if (filters.turnaround !== TurnaroundFilter.Over72) {
        if (
          prepCenter.turnaround == null ||
          prepCenter.turnaround === Turnaround.TurnaroundUnknown
        )
          return false;

        if ((TurnaroundSort[prepCenter.turnaround] ?? 0) > filters.turnaround)
          return false;
      }

      return true;
    });
  }, [prepCenters, filters]);

  const locations = useMemo((): PrepCenterLocation[] => {
    let locations: PrepCenterLocation[] = [];
    for (let i = 0; i < filteredPrepCenters.length; i++) {
      if (filteredPrepCenters[i].locations != null) {
        locations = locations.concat(
          filteredPrepCenters[i].locations?.filter(
            (l) => l.latitude != null && l.longitude != null
          ) as PrepCenterLocation[]
        );
      }
    }

    return locations;
  }, [filteredPrepCenters]);

  const sortedPrepCenters = (prepCenters: PrepCenter[]): PrepCenter[] => {
    return prepCenters.sort((a, b) => {
      let scoreA = a.reviewCount
        ? 0.95 * Math.pow(a.averageRating as number, 2.5) +
          0.05 * Math.pow(a.averageRating as number, 2.5) * a.reviewCount
        : 0;
      let scoreB = b.reviewCount
        ? 0.95 * Math.pow(b.averageRating as number, 2.5) +
          0.05 * Math.pow(b.averageRating as number, 2.5) * b.reviewCount
        : 0;
      return scoreB - scoreA;
    });
  };

  /*const sortedPrepCentersByProximity = (
    prepCenters: PrepCenter[],
    latLng: Number[]
  ): PrepCenter[] => {
    if (latLng.length !== 2) return [];

    return prepCenters.sort((a, b) => {
      return 0; // To be completed later...
    });
  };*/

  /**
   * Client-side sorting & filtering
   */
  const prepCenterList = useMemo(() => {
    const inViewBucket = [
      ...sortedPrepCenters(
        filteredPrepCenters.filter((prepCenter) => prepCenter.verified)
      ),
      ...sortedPrepCenters(
        filteredPrepCenters.filter(
          (prepCenter) =>
            hasLocationInViewport(prepCenter) && !prepCenter.verified
        )
      ),
    ];
    const outOfViewBucket = sortedPrepCenters(
      filteredPrepCenters.filter(
        (prepCenter) =>
          !hasLocationInViewport(prepCenter) && !prepCenter.verified
      )
    );

    let list = inViewBucket.map((prepCenter) =>
      prepCenterComponent(prepCenter)
    );
    if (list.length === 0) {
      list = [
        <div
          key="empty-in-view-bucket"
          className="relative block w-full border-2 border-gray-300 border-dashed rounded-lg p-8 text-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vendrive-500"
        >
          <MapIcon
            className="mx-auto h-12 w-12 text-gray-400"
            aria-hidden="true"
          />
          <span className="mt-2 block text-sm font-medium text-gray-900">
            No locations in visible map area
          </span>
        </div>,
      ];
    }
    if (outOfViewBucket.length > 0) {
      list = list.concat(
        [
          <div key="divider" className="relative">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-2 text-sm text-gray-500 bg-gray-50">
                More outside of map view
              </span>
            </div>
          </div>,
        ],
        outOfViewBucket.map((prepCenter) => prepCenterComponent(prepCenter))
      );
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasLocationInViewport,
    prepCenterComponent,
    filteredPrepCenters,
    viewState,
  ]);

  const prepCenterMap = useMemo(
    () => new Map<string, PrepCenter>(prepCenters.map((i) => [i.id, i])),
    [prepCenters]
  );

  const markers = useMemo(() => {
    return locations.map((location) => (
      <Marker
        key={location.id}
        location={location}
        prepCenter={prepCenterMap.get(location.prepCenterId) as PrepCenter}
        highlighted={highlighted}
      />
    ));
  }, [locations, prepCenterMap, highlighted]);

  const filterCount = useMemo(() => {
    let count = 0;
    if (filters.acceptingClients !== YesNoShowAllFilter.ShowAll) count++;
    if (filters.liftgateRequired !== YesNoShowAllFilter.ShowAll) count++;
    if (filters.supportedModels.length > 0) count++;
    if (filters.turnaround !== TurnaroundFilter.Over72) count++;
    return count;
  }, [filters]);

  useEffect(() => {
    async function doListPrepCenters() {
      const { status, prepCenters, errorMessage } = await listPrepCenters();
      if (status === 200) {
        setPrepCenters(prepCenters as PrepCenter[]);
      } else {
        setPrepCentersError(errorMessage as string);
      }
      setLoading(false);
    }
    doListPrepCenters();
  }, [listPrepCenters, setLoading, setPrepCenters]);

  return (
    <>
      {/* Content area */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        <header className="bg-white shadow-sm z-20">
          {/* Filters */}
          <Disclosure
            as="section"
            aria-labelledby="filter-heading"
            className="grid relative z-10 border-b border-gray-200 items-center"
          >
            <h2 id="filter-heading" className="sr-only">
              Filters
            </h2>

            <div className="flex justify-between sticky top-0 py-4 border-b border-gray-200 bg-white">
              <div className="flex space-x-6 divide-x divide-gray-200 text-sm px-4 sm:px-6 lg:px-8">
                <div>
                  <Disclosure.Button className="group text-gray-700 font-medium flex items-center">
                    <FilterIcon
                      className="flex-none w-5 h-5 mr-2 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    {filterCount ? `${filterCount} Filters` : "Filters"}
                  </Disclosure.Button>
                </div>
                <div className={filterCount ? "pl-6" : "hidden"}>
                  <button className="text-gray-500" onClick={clearFilters}>
                    Clear all
                  </button>
                </div>
              </div>

              <div className="flex justify-end px-4 sm:px-6 lg:px-8">
                <div className="relative inline-block">
                  <p className="text-sm font-medium text-gray-400">
                    {filteredPrepCenters.length
                      ? `${filteredPrepCenters.length} result${
                          filteredPrepCenters.length > 1 ? "s" : ""
                        }`
                      : ""}
                  </p>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="pt-10 pb-24 sm:py-10">
              <div className="max-w-7xl mx-auto grid grid-cols-2 gap-x-4 px-4 text-sm sm:px-6 md:gap-x-6 lg:px-8">
                <div className="grid grid-cols-1 gap-y-10 auto-rows-min md:grid-cols-2 md:gap-x-6">
                  <fieldset>
                    <legend className="block font-medium">
                      Accepting clients
                    </legend>
                    <div className="pt-6 space-y-6 sm:pt-4 sm:space-y-4">
                      <div className="flex items-center">
                        <input
                          id="accepting-clients-yes"
                          name="accepting-clients"
                          type="radio"
                          value={YesNoShowAllFilter.Yes}
                          checked={
                            filters.acceptingClients === YesNoShowAllFilter.Yes
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              acceptingClients: e.target
                                .value as YesNoShowAllFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="accepting-clients-yes"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="accepting-clients-no"
                          name="accepting-clients"
                          type="radio"
                          value={YesNoShowAllFilter.No}
                          checked={
                            filters.acceptingClients === YesNoShowAllFilter.No
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              acceptingClients: e.target
                                .value as YesNoShowAllFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="accepting-clients-no"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          No
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="accepting-clients-show-all"
                          name="accepting-clients"
                          type="radio"
                          value={YesNoShowAllFilter.ShowAll}
                          checked={
                            filters.acceptingClients ===
                            YesNoShowAllFilter.ShowAll
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              acceptingClients: e.target
                                .value as YesNoShowAllFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="accepting-clients-show-all"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          Show all
                        </label>
                      </div>
                    </div>
                  </fieldset>

                  <fieldset>
                    <legend className="block font-medium">
                      Liftgate required
                    </legend>
                    <div className="pt-6 space-y-6 sm:pt-4 sm:space-y-4">
                      <div className="flex items-center">
                        <input
                          id="liftgate-required-yes"
                          name="liftgate-required"
                          type="radio"
                          value={YesNoShowAllFilter.Yes}
                          checked={
                            filters.liftgateRequired === YesNoShowAllFilter.Yes
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              liftgateRequired: e.target
                                .value as YesNoShowAllFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="liftgate-required-yes"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="liftgate-required-no"
                          name="liftgate-required"
                          type="radio"
                          value={YesNoShowAllFilter.No}
                          checked={
                            filters.liftgateRequired === YesNoShowAllFilter.No
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              liftgateRequired: e.target
                                .value as YesNoShowAllFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="liftgate-required-no"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          No
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="liftgate-required-show-all"
                          name="liftgate-required"
                          type="radio"
                          value={YesNoShowAllFilter.ShowAll}
                          checked={
                            filters.liftgateRequired ===
                            YesNoShowAllFilter.ShowAll
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              liftgateRequired: e.target
                                .value as YesNoShowAllFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="liftgate-required-show-all"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          Show all
                        </label>
                      </div>
                    </div>
                  </fieldset>
                </div>

                <div className="grid grid-cols-1 gap-y-10 auto-rows-min md:grid-cols-2 md:gap-x-6">
                  <fieldset>
                    <legend className="block font-medium">
                      Supported models
                    </legend>
                    <div className="pt-6 space-y-6 sm:pt-4 sm:space-y-4">
                      {SupportedModelDetails.map((supportedModelDetail) => (
                        <div
                          key={supportedModelDetail.value}
                          className="flex items-center text-base sm:text-sm"
                        >
                          <input
                            id={supportedModelDetail.value}
                            name={supportedModelDetail.value}
                            type="checkbox"
                            checked={
                              filters.supportedModels.includes(
                                supportedModelDetail.value
                              ) || false
                            }
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                supportedModels: e.target.checked
                                  ? Array.from(
                                      new Set([
                                        ...filters.supportedModels,
                                        supportedModelDetail.value,
                                      ])
                                    )
                                  : filters.supportedModels.filter(
                                      (m: SupportedModel) =>
                                        m !== supportedModelDetail.value
                                    ),
                              })
                            }
                            className="flex-shrink-0 h-4 w-4 border-gray-300 rounded text-vendrive-600 focus:ring-vendrive-500"
                          />
                          <label
                            htmlFor={supportedModelDetail.value}
                            className="ml-3 min-w-0 flex-1 text-gray-600"
                          >
                            {supportedModelDetail.title}
                          </label>
                        </div>
                      ))}
                    </div>
                  </fieldset>

                  <fieldset>
                    <legend className="block font-medium">
                      Max turnaround time
                    </legend>
                    <div className="pt-6 space-y-6 sm:pt-4 sm:space-y-4">
                      <div className="flex items-center">
                        <input
                          id="turnaround-within-24"
                          name="turnaround"
                          type="radio"
                          value={TurnaroundFilter.Within24}
                          checked={
                            filters.turnaround === TurnaroundFilter.Within24
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              turnaround: parseInt(
                                e.target.value,
                                10
                              ) as TurnaroundFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="turnaround-within-24"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          Within 24 hours
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="turnaround-within-48"
                          name="turnaround"
                          type="radio"
                          value={TurnaroundFilter.Within48}
                          checked={
                            filters.turnaround === TurnaroundFilter.Within48
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              turnaround: parseInt(
                                e.target.value,
                                10
                              ) as TurnaroundFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="turnaround-within-48"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          Within 48 hours
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="turnaround-within-72"
                          name="turnaround"
                          type="radio"
                          value={TurnaroundFilter.Within72}
                          checked={
                            filters.turnaround === TurnaroundFilter.Within72
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              turnaround: parseInt(
                                e.target.value,
                                10
                              ) as TurnaroundFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="turnaround-within-72"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          Within 72 hours
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="turnaround-over-72"
                          name="turnaround"
                          type="radio"
                          value={TurnaroundFilter.Over72}
                          checked={
                            filters.turnaround === TurnaroundFilter.Over72
                          }
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              turnaround: parseInt(
                                e.target.value,
                                10
                              ) as TurnaroundFilter,
                            })
                          }
                          className="focus:ring-vendrive-500 h-4 w-4 text-vendrive-600 border-gray-300"
                        />
                        <label
                          htmlFor="turnaround-over-72"
                          className="ml-3 block text-base sm:text-sm text-gray-600"
                        >
                          Over 72 hours
                        </label>
                      </div>
                    </div>
                  </fieldset>
                </div>
              </div>
            </Disclosure.Panel>

            {/* Sorting */}
            {/*<div className="col-start-1 row-start-1 py-4">
              <div className="flex justify-end px-4 sm:px-6 lg:px-8">
                <div className="relative inline-block">
                  <p className="text-sm font-medium text-gray-400">
                    {filteredPrepCenters.length
                      ? `${filteredPrepCenters.length} result${
                          filteredPrepCenters.length > 1 ? "s" : ""
                        }`
                      : ""}
                  </p>
                </div>
                <Menu as="div" className="relative inline-block">
                  <div className="flex">
                    <Menu.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                      Sort
                      <ChevronDownIcon
                        className="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                  </div>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        {sortOptions.map((option) => (
                          <Menu.Item key={option.name}>
                            {({ active }) => (
                              <a
                                href={option.href}
                                className={classNames(
                                  option.current
                                    ? "font-medium text-gray-900"
                                    : "text-gray-500",
                                  active ? "bg-gray-100" : "",
                                  "block px-4 py-2 text-sm"
                                )}
                              >
                                {option.name}
                              </a>
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>*/}
          </Disclosure>
        </header>

        {/* Main content */}
        <div className="flex-1 flex items-stretch overflow-hidden">
          <main className="flex-1 overflow-y-auto">
            {/* Primary column */}
            <section
              aria-labelledby="primary-heading"
              className="min-w-0 flex-1 h-full flex flex-col lg:order-last"
            >
              <h1 id="primary-heading" className="sr-only">
                Map
              </h1>

              <MapGL
                {...viewState}
                ref={setRef as Ref<MapRef>}
                onMove={(evt) => setViewState(evt.viewState)}
                mapStyle="mapbox://styles/prepcenter/cl4eone8z00a615ozm518ldnp"
                mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN}
              >
                {markers}
              </MapGL>
            </section>
          </main>

          <aside
            className={classNames(
              "w-full sm:max-w-md lg:max-w-[720px] shadow-lg z-10 overflow-y-auto",
              selectedView === 0 ? "hidden sm:block" : "block"
            )}
          >
            {prepCentersError ? (
              <div>
                <ErrorCallout subTitle={prepCentersError}></ErrorCallout>
              </div>
            ) : null}
            <ul role="list" className="space-y-3 p-3">
              {prepCenterList}
            </ul>
          </aside>
        </div>
      </div>

      <div className="sm:hidden fixed z-20 bottom-0 inset-x-0 pb-3 mx-auto w-full max-w-xs px-2 py-16">
        <Tab.Group selectedIndex={selectedView} onChange={setSelectedView}>
          <Tab.List className="flex space-x-1 rounded-xl bg-vendrive-900/70 p-1">
            <Tab
              className={({ selected }) =>
                classNames(
                  "w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-vendrive-700 flex justify-center items-center",
                  "ring-white ring-opacity-60 ring-offset-2 ring-offset-vendrive-400 focus:outline-none focus:ring-2",
                  selected
                    ? "bg-white shadow"
                    : "text-vendrive-100 hover:bg-white/[0.12] hover:text-white"
                )
              }
            >
              <FontAwesomeIcon
                icon={["fad", "map-location-dot"]}
                className="text-base mr-2"
              />
              Map view
            </Tab>

            <Tab
              className={({ selected }) =>
                classNames(
                  "w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-vendrive-700 flex justify-center items-center",
                  "ring-white ring-opacity-60 ring-offset-2 ring-offset-vendrive-400 focus:outline-none focus:ring-2",
                  selected
                    ? "bg-white shadow"
                    : "text-vendrive-100 hover:bg-white/[0.12] hover:text-white"
                )
              }
            >
              <FontAwesomeIcon
                icon={["fad", "list"]}
                className="text-base mr-2"
              />
              List view
            </Tab>
          </Tab.List>
        </Tab.Group>
      </div>
    </>
  );
}

Home.getLayout = function getLayout(page: ReactElement) {
  return <SearchLayout title="Search">{page}</SearchLayout>;
};

export async function getStaticProps() {
  return { props: { overflowHidden: true } };
}
